import React, { useState, useRef, useEffect } from "react";
import "./CardDetailModal.css";
import ConfirmModal from "./ConfirmModal";
import { supabase } from "./supabaseClient";
import { formatCurrency } from "./services/formatters";
import { formatItemYear } from "./services/formatItemYear";
import { deleteCardImage } from "./services/supabaseActions";

function CardDetailModal({ card, onClose, onEdit, onDelete, onImagesChanged, isDarkMode }) {
  const [zoomed, setZoomed] = useState(false);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const imgRef = useRef(null);
  const [images, setImages] = useState([]);
  const swipeStartX = useRef(0);
  const swipeThreshold = 50;
  const dragging = useRef(false);
  const startDrag = useRef({ x: 0, y: 0 });
  const startOffset = useRef({ x: 0, y: 0 });
  const lastTap = useRef(0);
  const orderSaveQueue = useRef(Promise.resolve());

  // Estimated value & listings
  const [estimatedValue, setEstimatedValue] = useState(card?.estimated_value ?? null);
  const [loadingEstimatedValue, setLoadingEstimatedValue] = useState(false);
  const [compsFound, setCompsFound] = useState(0);
  const [totalCompsFound, setTotalCompsFound] = useState(0);

  const [minPrice, setMinPrice] = useState(null);
  const [maxPrice, setMaxPrice] = useState(null);
  const [medianPrice, setMedianPrice] = useState(null);

  const [listings, setListings] = useState([]);
  const [allListings, setAllListings] = useState([]);

  const [gradeBreakdown, setGradeBreakdown] = useState({});
  const [valuationFilter, setValuationFilter] = useState(null);

  const [cached, setCached] = useState(false);
  const [showPriceDetails, setShowPriceDetails] = useState(false);

  const [actionMessage, setActionMessage] = useState("");

  const modalClass = isDarkMode ? "hobbli-card-detail dark" : "hobbli-card-detail";
  const currentImage = images[selectedImageIndex];
  const displayImage = currentImage?.image_url || "/placeholder-card.png";



  // ------------------ Fetch Estimated Value ------------------
  // ------------------ Fetch Estimated Value ------------------
  useEffect(() => {
    if (!card?.id) return;

    const fetchEstimatedValue = async () => {
      try {
        setLoadingEstimatedValue(true);

        const res = await fetch(
          `https://ftzldtvhfnbrsrzknlxn.supabase.co/functions/v1/get-comps?user_item_id=${card.id}`
        );

        if (!res.ok) {
          console.error(
            "Failed to fetch comps:",
            res.status,
            res.statusText
          );

          setEstimatedValue(null);
          setCompsFound(0);
          setTotalCompsFound(0);
          setListings([]);
          setAllListings([]);
          setGradeBreakdown({});
          setValuationFilter(null);

          return;
        }

        const data = await res.json();

        console.log("COMP RESPONSE:", data);

        if (data.estimated_value != null) {
          setEstimatedValue(
            Number(data.estimated_value).toFixed(2)
          );

          // Listings actually used to value THIS card
          setCompsFound(
            data.comps_found ?? 0
          );

          // Entire eBay result pool before contextual filtering
          setTotalCompsFound(
            data.total_comps_found ??
              data.all_listings?.length ??
              data.comps_found ??
              0
          );

          setMinPrice(
            data.min_price ?? null
          );

          setMaxPrice(
            data.max_price ?? null
          );

          setMedianPrice(
            data.median_price ?? null
          );

          // Contextual comps used for this item's valuation
          setListings(
            data.listings ?? []
          );

          // Full generalized comp pool
          setAllListings(
            data.all_listings ??
              data.listings ??
              []
          );

          // Future "If graded..." analysis
          setGradeBreakdown(
            data.grade_breakdown ?? {}
          );

          // Information about which valuation filter was applied
          setValuationFilter(
            data.valuation_filter ?? null
          );

          setCached(
            data.cached ?? false
          );
        } else {
          setEstimatedValue(null);

          setCompsFound(0);

          setTotalCompsFound(
            data.total_comps_found ?? 0
          );

          setMinPrice(null);
          setMaxPrice(null);
          setMedianPrice(null);

          setListings([]);

          setAllListings(
            data.all_listings ?? []
          );

          setGradeBreakdown(
            data.grade_breakdown ?? {}
          );

          setValuationFilter(
            data.valuation_filter ?? null
          );

          setCached(
            data.cached ?? false
          );
        }
      } catch (err) {
        console.error(
          "Error fetching estimated value:",
          err
        );

        setEstimatedValue(null);
        setCompsFound(0);
        setTotalCompsFound(0);

        setMinPrice(null);
        setMaxPrice(null);
        setMedianPrice(null);

        setListings([]);
        setAllListings([]);

        setGradeBreakdown({});
        setValuationFilter(null);
        setCached(false);
      } finally {
        // Critical: stop displaying "Updating..."
        setLoadingEstimatedValue(false);
      }
    };

    fetchEstimatedValue();
  }, [card?.id]);

  useEffect(() => {
    const normalized = (card?.images || [])
      .map((img, index) => {
        if (!img) return null;

        if (typeof img === "string") {
          return {
            id: index,
            image_url: img,
            position: index,
          };
        }

        return {
          id: img.id ?? index,
          image_url: img.image_url ?? img.url,
          position: img.position ?? index,
        };
      })
      .filter(Boolean)
      .sort(
        (a, b) =>
          (a.position ?? 0) - (b.position ?? 0)
      );

    setImages(normalized);
    setSelectedImageIndex(0);
  }, [card?.images]);

  // ------------------ Zoom & Drag Handlers ------------------
  const clampOffset = (value, limit) => {
    const max = limit * 1.5;
    if (value > max) return max;
    if (value < -max) return -max;
    return value;
  };
  const clampScale = (s) => {
    const min = 1, max = 5, overshoot = 0.3;
    if (s < min - overshoot) return min - overshoot;
    if (s > max + overshoot) return max + overshoot;
    return s;
  };
  const handleMouseDown = e => {
    if (!zoomed) return;
    e.preventDefault();
    dragging.current = true;
    startDrag.current = { x: e.clientX, y: e.clientY };
    startOffset.current = { ...offset };
  };
  const handleMouseMove = e => {
    if (!dragging.current) return;
    const dx = e.clientX - startDrag.current.x;
    const dy = e.clientY - startDrag.current.y;
    const img = imgRef.current;
    if (!img) return;
    const maxX = (img.width * (scale - 1)) / 2;
    const maxY = (img.height * (scale - 1)) / 2;
    setOffset({ x: clampOffset(startOffset.current.x + dx, maxX), y: clampOffset(startOffset.current.y + dy, maxY) });
  };
  const handleMouseUp = () => (dragging.current = false);
  const handleTouchStart = e => {
    if (!zoomed) return;
    if (e.touches.length === 1) {
      swipeStartX.current = e.touches[0].clientX;
      const now = Date.now();
      if (now - lastTap.current < 300) {
        setScale(scale === 1 ? 2 : 1);
        if (scale !== 1) setOffset({ x: 0, y: 0 });
        lastTap.current = 0;
        return;
      }
      lastTap.current = now;
      dragging.current = true;
      startDrag.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      startOffset.current = { ...offset };
    }
  };
  const handleTouchMove = e => {
    if (!zoomed) return;
    const img = imgRef.current;
    if (!img) return;
    if (e.touches.length === 1 && dragging.current) {
      const dx = e.touches[0].clientX - startDrag.current.x;
      const dy = e.touches[0].clientY - startDrag.current.y;
      const maxX = (img.width * (scale - 1)) / 2;
      const maxY = (img.height * (scale - 1)) / 2;
      setOffset({ x: clampOffset(startOffset.current.x + dx, maxX), y: clampOffset(startOffset.current.y + dy, maxY) });
    } else if (e.touches.length === 2) {
      e.preventDefault();
      const [t1, t2] = e.touches;
      const distance = Math.hypot(t2.pageX - t1.pageX, t2.pageY - t1.pageY);
      if (!img.startDistance) {
        img.startDistance = distance;
        img.startScale = scale;
      } else {
        setScale(clampScale((distance / img.startDistance) * img.startScale));
      }
    }
  };
  const handleTouchEnd = e => {
    dragging.current = false;
    if (e.changedTouches?.length === 1 && !zoomed) {
      const dx = e.changedTouches[0].clientX - swipeStartX.current;
      if (dx > swipeThreshold && selectedImageIndex > 0) setSelectedImageIndex(selectedImageIndex - 1);
      else if (dx < -swipeThreshold && selectedImageIndex < images.length - 1) setSelectedImageIndex(selectedImageIndex + 1);
    }
    const img = imgRef.current;
    if (!img) return;
    img.startDistance = null;
    img.startScale = null;
    if (scale < 1) setScale(1);
    if (scale > 5) setScale(5);
  };
  const handleDoubleClick = () => {
    if (!zoomed) return;
    if (scale === 1) setScale(2);
    else { setScale(1); setOffset({ x: 0, y: 0 }); }
  };
  const handleClose = () => { setZoomed(false); setScale(1); setOffset({ x: 0, y: 0 }); };

  const handleDeleteImage = async () => {
    if (!confirmDelete) return;

    try {
      const { url, imageId, userItemId } = confirmDelete;

      await deleteCardImage({
        imageId,       
        url,
        userItemId
      });

      const updatedImages = images
        .filter(
          (img) =>
            String(img.id) !== String(imageId)
        )
        .map((img, index) => ({
          ...img,
          position: index,
        }));

      setImages(updatedImages);

      setSelectedImageIndex((prev) =>
        Math.min(
          prev,
          Math.max(updatedImages.length - 1, 0)
        )
      );

      setConfirmDelete(null);

      await onImagesChanged?.(card.id);
      setActionMessage("");

    } catch (err) {
      console.error(err);
      setActionMessage("Failed to delete the image. Please try again.");
    }
  };

  const saveImageOrder = async (updatedImages) => {
    const payload = updatedImages.map((img, index) => ({
      id: img.id,
      position: index,
    }));

    const { error } = await supabase.rpc("reorder_item_images", { payload });

    if (error) {
      console.error("reorder failed:", error);
      return;
    }

    await onImagesChanged?.(card.id);
  };

  const scheduleImageOrderSave = (updatedImages) => {
    // Serialize reorder writes so rapid arrow clicks cannot let an older RPC
    // finish after a newer one and overwrite the final order.
    orderSaveQueue.current = orderSaveQueue.current
      .catch(() => undefined)
      .then(() => saveImageOrder(updatedImages));
  };

  const moveImage = (from, to) => {
    if (from === to || to < 0 || to >= images.length) return;

    setImages((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(from, 1);
      updated.splice(to, 0, moved);

      const reindexed = updated.map((img, index) => ({
        ...img,
        position: index,
      }));

      scheduleImageOrderSave(reindexed);
      setSelectedImageIndex(to);
      return reindexed;
    });
  };

  // ------------------ Modal Rendering ------------------
  if (!card) return null;
  const itemTitle = card.name || card.suggested_name || "Untitled item";
  const itemMeta = [formatItemYear(card.year, card.year_end), card.brand, card.set_name].filter(Boolean).join(" • ");
  const variantText =
    Array.isArray(card.variants) && card.variants.length > 0
      ? card.variants
          .map((variant) => (typeof variant === "string" ? variant : variant?.name))
          .filter(Boolean)
          .join(", ")
      : null;

  const itemGrade = card.grade ?? card.attributes?.grade ?? null;
  const autoGrade = card.autograph_grade ?? card.attributes?.autograph_grade ?? null;
  const gradingCompany =
    card.grading_company ?? card.attributes?.grading_company ?? null;
  const certNumber = card.cert_number ?? card.attributes?.cert_number ?? null;
  const serialNumber = card.serial_number ?? card.attributes?.serial_number ?? null;
  const numberedVariant = (Array.isArray(card.variants) ? card.variants : [])
    .map((variant) => (typeof variant === "string" ? variant : variant?.name))
    .filter(Boolean)
    .map((name) => String(name).match(/\/\s*(\d+)\s*$/))
    .find(Boolean);
  const serialDisplay =
    serialNumber !== null && serialNumber !== undefined && serialNumber !== ""
      ? numberedVariant
        ? `${serialNumber}/${numberedVariant[1]}`
        : String(serialNumber)
      : null;

  const hasGradingDetails = Boolean(
    card.graded && (gradingCompany || itemGrade || autoGrade || certNumber)
  );

  const relatedListings =
    Array.isArray(allListings) && allListings.length > 0
      ? allListings.filter((related) => {
          if (!Array.isArray(listings) || listings.length === 0) return true;

          const relatedId =
            related?.itemId ??
            related?.item_id ??
            related?.source_listing_id ??
            related?.id ??
            null;

          if (relatedId != null) {
            return !listings.some((listing) => {
              const listingId =
                listing?.itemId ??
                listing?.item_id ??
                listing?.source_listing_id ??
                listing?.id ??
                null;
              return listingId != null && String(listingId) === String(relatedId);
            });
          }

          return !listings.some(
            (listing) =>
              listing?.title === related?.title &&
              Number(listing?.price) === Number(related?.price)
          );
        })
      : [];

  const renderDetail = (label, value, extraClass = "") => {
    if (value === null || value === undefined || value === "") return null;

    return (
      <div className={`card-detail-field ${extraClass}`.trim()}>
        <span className="card-detail-field-label">{label}</span>
        <span className="card-detail-field-value">{value}</span>
      </div>
    );
  };

  const showPreviousImage = (event) => {
    event?.stopPropagation();
    if (images.length < 2) return;
    setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const showNextImage = (event) => {
    event?.stopPropagation();
    if (images.length < 2) return;
    setSelectedImageIndex((prev) => (prev + 1) % images.length);
  };

  const handleGalleryTouchStart = (event) => {
    if (event.touches?.length === 1) {
      swipeStartX.current = event.touches[0].clientX;
    }
  };

  const handleGalleryTouchEnd = (event) => {
    if (images.length < 2 || event.changedTouches?.length !== 1) return;
    const dx = event.changedTouches[0].clientX - swipeStartX.current;
    if (dx > swipeThreshold) showPreviousImage();
    if (dx < -swipeThreshold) showNextImage();
  };

  return (
    <div
      className="card-detail-shell"
      role="dialog"
      aria-modal="true"
      aria-label={itemTitle}
    >
      <div className={modalClass}>
        <button
          type="button"
          className="card-detail-close"
          onClick={onClose}
          aria-label="Close item details"
          title="Close"
        >
          ×
        </button>


        {actionMessage && (
          <div role="alert" style={{ margin: "10px 52px 12px 16px", padding: "10px 12px", borderRadius: 8, background: "rgba(180,35,24,.10)", color: "#b42318", fontSize: ".86rem" }}>
            {actionMessage}
          </div>
        )}

        {/* Zoomed Image Overlay */}
        {zoomed && currentImage && (
          <div
            className="image-zoom-overlay"
            onClick={handleClose}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchStart={handleTouchStart}
          >
            <button
              type="button"
              className="image-zoom-close"
              onClick={handleClose}
              aria-label="Close enlarged image"
            >
              ×
            </button>
            <img
              ref={imgRef}
              src={displayImage}
              alt={itemTitle}
              className="image-zoomed"
              style={{
                transform: `scale(${scale}) translate(${offset.x / scale}px, ${offset.y / scale}px)`,
                transition: "transform 0.25s ease-out",
                cursor: zoomed ? "grab" : "auto",
                touchAction: "none",
              }}
              onClick={(event) => event.stopPropagation()}
              onMouseDown={handleMouseDown}
              onDoubleClick={handleDoubleClick}
            />
          </div>
        )}

        {/* Confirm Delete Modal */}
        {confirmDelete && (
          <ConfirmModal
            message="Delete this image?"
            onConfirm={handleDeleteImage}
            onCancel={() => setConfirmDelete(null)}
          />
        )}

        <div className="card-detail-layout">
          {/* Gallery */}
          <section className="card-detail-gallery" aria-label="Item photos">
            <div
              className={`card-detail-image-stage ${currentImage ? "is-zoomable" : ""}`}
              onClick={() => currentImage && setZoomed(true)}
              onTouchStart={handleGalleryTouchStart}
              onTouchEnd={handleGalleryTouchEnd}
            >
              <img
                src={displayImage}
                alt={itemTitle}
                className="card-detail-image"
              />

              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    className="card-detail-gallery-nav card-detail-gallery-nav--prev"
                    onClick={showPreviousImage}
                    aria-label="Previous image"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="card-detail-gallery-nav card-detail-gallery-nav--next"
                    onClick={showNextImage}
                    aria-label="Next image"
                  >
                    ›
                  </button>
                  <div className="card-detail-image-count">
                    {selectedImageIndex + 1} / {images.length}
                  </div>
                </>
              )}

              {currentImage && (
                <div className="card-detail-zoom-hint">Click to enlarge</div>
              )}
            </div>

            {Array.isArray(images) && images.length > 1 && (
              <div className="card-thumbnails" aria-label="Choose item image">
                {images.map((img, i) => {
                  const imgUrl = img?.image_url;
                  if (!imgUrl) return null;

                  return (
                    <div
                      key={img.id || i}
                      className={`thumbnail-wrapper ${
                        i === selectedImageIndex ? "is-selected" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="thumbnail-image-button"
                        onClick={() => setSelectedImageIndex(i)}
                        aria-label={`Show image ${i + 1}`}
                      >
                        <img
                          src={imgUrl}
                          alt={`${itemTitle} view ${i + 1}`}
                          className="thumbnail"
                        />
                      </button>

                      <div className="thumbnail-controls">
                        {i > 0 && (
                          <button
                            type="button"
                            className="thumbnail-control"
                            onClick={() => moveImage(i, i - 1)}
                            aria-label={`Move image ${i + 1} left`}
                            title="Move left"
                          >
                            ←
                          </button>
                        )}

                        <button
                          type="button"
                          className="thumbnail-control thumbnail-control--danger"
                          onClick={() =>
                            setConfirmDelete({
                              index: i,
                              url: imgUrl,
                              imageId: img.id,
                              userItemId: card.id,
                            })
                          }
                          aria-label={`Delete image ${i + 1}`}
                          title="Delete image"
                        >
                          ×
                        </button>

                        {i < images.length - 1 && (
                          <button
                            type="button"
                            className="thumbnail-control"
                            onClick={() => moveImage(i, i + 1)}
                            aria-label={`Move image ${i + 1} right`}
                            title="Move right"
                          >
                            →
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Details */}
          <section className="card-detail-content">
            <header className="card-detail-header">
              <span className="card-detail-kicker">Collection item</span>
              <h2 className="card-detail-title">{itemTitle}</h2>
              {itemMeta && <p className="card-detail-meta">{itemMeta}</p>}

              {card.autographed && (
                <div className="card-detail-badges">
                  <span className="card-detail-badge">Autographed</span>
                </div>
              )}
            </header>

            <div className="card-detail-section">
              <div className="card-detail-section-heading">
                <h3>Item details</h3>
              </div>
              <div className="card-detail-fields">
                {renderDetail("Set", card.set_name)}
                {renderDetail("Subset / Insert", card.subset)}
                {renderDetail("Card #", card.card_number)}
                {renderDetail("Variant / Parallel", variantText)}
                {renderDetail("Serial Number", serialDisplay)}
                {renderDetail(
                  "Price paid",
                  card.price_paid != null ? formatCurrency(card.price_paid) : null
                )}
              </div>
            </div>

            {hasGradingDetails && (
              <div className="card-detail-section card-detail-grading-section">
                <div className="card-detail-section-heading">
                  <h3>Grading</h3>
                </div>
                <div className="card-detail-grading-card">
                  <div className="card-detail-grade-primary">
                    <span className="card-detail-field-label">Item Grade</span>
                    <strong>{itemGrade || "—"}</strong>
                  </div>
                  <div className="card-detail-grade-meta">
                    {renderDetail("Company", gradingCompany, "card-detail-field--compact")}
                    {renderDetail("Auto Grade", autoGrade, "card-detail-field--compact")}
                    {renderDetail("Certification #", certNumber, "card-detail-field--compact")}
                  </div>
                </div>
              </div>
            )}

            <div className="card-detail-value-card">
              <div className="card-detail-value-copy">
                <span className="card-detail-field-label">Estimated value</span>
                <div className="card-detail-value-amount">
                  {loadingEstimatedValue
                    ? "Updating…"
                    : estimatedValue != null
                    ? formatCurrency(estimatedValue)
                    : "Not available"}
                </div>
                {!loadingEstimatedValue && (
                  <span className="card-detail-value-supporting">
                    {estimatedValue != null && compsFound > 0 ? (
                      totalCompsFound > compsFound ? (
                        <>
                          Based on {compsFound} comparable active listing
                          {compsFound === 1 ? "" : "s"} from {totalCompsFound} market listings found
                        </>
                      ) : (
                        <>
                          Based on {compsFound} comparable active listing
                          {compsFound === 1 ? "" : "s"}
                        </>
                      )
                    ) : totalCompsFound > 0 ? (
                      <>
                        No exact estimate available • {totalCompsFound} related active listing
                        {totalCompsFound === 1 ? "" : "s"} found
                      </>
                    ) : (
                      "No comparable active listings found"
                    )}
                  </span>
                )}
              </div>

              {!loadingEstimatedValue && (estimatedValue != null || totalCompsFound > 0) && (
                <button
                  type="button"
                  className="card-detail-value-button"
                  onClick={() => setShowPriceDetails(true)}
                >
                  {estimatedValue != null ? "View market data" : "View related market data"}
                </button>
              )}
            </div>

            {card.notes && (
              <div className="card-detail-notes">
                <span className="card-detail-field-label">Notes</span>
                <p>{card.notes}</p>
              </div>
            )}
          </section>
        </div>

        {showPriceDetails && (
          <div className="card-detail-submodal-overlay">
            <div
              className={`price-details-modal ${isDarkMode ? "dark" : "light"}`}
              role="dialog"
              aria-modal="true"
            >
              <div className="card-detail-submodal-header">
                <div>
                  <span className="card-detail-kicker">Comparable listings</span>
                  <h3>Market data & estimated value</h3>
                </div>
                <button
                  type="button"
                  className="card-detail-submodal-close"
                  onClick={() => setShowPriceDetails(false)}
                  aria-label="Close estimated value details"
                >
                  ×
                </button>
              </div>

              <div className="price-summary-grid">
                <div className="price-summary-card price-summary-card--featured">
                  <span>Average</span>
                  <strong>
                    {estimatedValue != null ? formatCurrency(estimatedValue) : "N/A"}
                  </strong>
                </div>
                <div className="price-summary-card">
                  <span>Median</span>
                  <strong>{medianPrice != null ? formatCurrency(medianPrice) : "N/A"}</strong>
                </div>
                <div className="price-summary-card">
                  <span>Range</span>
                  <strong>
                    {minPrice != null ? formatCurrency(minPrice) : "N/A"} – {maxPrice != null ? formatCurrency(maxPrice) : "N/A"}
                  </strong>
                </div>
                <div className="price-summary-card">
                  <span>Comparable listings</span>
                  <strong>{compsFound}</strong>
                </div>
              </div>
              {!valuationFilter?.graded &&
                Object.keys(gradeBreakdown || {}).length > 0 && (
                  <div className="grade-breakdown-section">
                    <div className="card-detail-section-heading">
                      <div>
                        <span className="card-detail-kicker">
                          Grading potential
                        </span>

                        <h3>If graded...</h3>
                      </div>
                    </div>

                    <p className="grade-breakdown-description">
                      These are current asking prices for comparable graded
                      listings found in the same search. They do not predict
                      what grade your card would receive.
                    </p>

                    <div className="grade-breakdown-grid">
                      {Object.entries(gradeBreakdown)
                        .sort(([gradeA], [gradeB]) => Number(gradeA) - Number(gradeB))
                        .map(([grade, stats]) => (
                          <div
                            className="grade-breakdown-card"
                            key={grade}
                          >
                            <span className="grade-breakdown-grade">
                              Grade {grade}
                            </span>

                            <strong className="grade-breakdown-value">
                              {stats.median != null
                                ? formatCurrency(stats.median)
                                : stats.average != null
                                ? formatCurrency(stats.average)
                                : "—"}
                            </strong>

                            <span className="grade-breakdown-count">
                              {stats.count} comparable listing
                              {stats.count === 1 ? "" : "s"}
                            </span>

                            {stats.min != null && stats.max != null && (
                              <span className="grade-breakdown-range">
                                {formatCurrency(stats.min)} –{" "}
                                {formatCurrency(stats.max)}
                              </span>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              <p className="price-disclaimer">
                Hobbli estimates market value using comparable active eBay listings. These are seller asking prices, not completed sales, and may differ from actual transaction prices.
              </p>

              {compsFound > 0 ? (
                <div className="price-table-wrapper">
                  <table className="price-table">
                  <thead>
                    <tr>
                      <th>Listing</th>
                      <th>Price</th>
                      <th>Condition</th>
                      <th>Seller</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listings.map((listing, index) => (
                      <tr key={index}>
                        <td>{listing.title}</td>
                        <td>{listing.price != null ? formatCurrency(listing.price) : "N/A"}</td>
                        <td>{listing.condition || "—"}</td>
                        <td>{listing.seller || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                  </table>
                </div>
              ) : relatedListings.length > 0 ? (
                <div className="related-market-section">
                  <div className="price-empty-state">
                    <strong>No exact estimate available</strong>
                    <span>
                      Hobbli found {relatedListings.length} related active listing
                      {relatedListings.length === 1 ? "" : "s"}, but none matched this
                      item's valuation criteria closely enough to calculate an estimate.
                      These listings are shown for context only.
                    </span>
                  </div>

                  <div className="related-market-heading">
                    <span className="card-detail-kicker">Related market evidence</span>
                    <h3>Related active listings</h3>
                  </div>

                  <div className="price-table-wrapper">
                    <table className="price-table">
                      <thead>
                        <tr>
                          <th>Listing</th>
                          <th>Price</th>
                          <th>Condition</th>
                          <th>Seller</th>
                        </tr>
                      </thead>
                      <tbody>
                        {relatedListings.map((listing, index) => (
                          <tr key={index}>
                            <td>{listing.title}</td>
                            <td>
                              {listing.price != null
                                ? formatCurrency(listing.price)
                                : "N/A"}
                            </td>
                            <td>{listing.condition || "—"}</td>
                            <td>{listing.seller || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="price-empty-state">
                  <strong>No comparable active listings found</strong>
                  <span>
                    Hobbli did not find enough relevant active listings to calculate
                    an estimate or provide related market evidence.
                  </span>
                </div>
              )}

              <div className="price-details-footer">
                <span className="price-cache-status">
                  {cached ? "Stored market data" : "Updated market data"}
                </span>
                <button
                  type="button"
                  className="card-detail-action card-detail-action--primary"
                  onClick={() => setShowPriceDetails(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="card-detail-actions">
          <div className="card-detail-actions-primary">
            <button
              type="button"
              onClick={() => onEdit(card)}
              className="card-detail-action card-detail-action--primary"
            >
              Edit item
            </button>

          </div>

          <div className="card-detail-actions-secondary">
            <button
              type="button"
              onClick={() => onDelete(card)}
              className="card-detail-action card-detail-action--danger-ghost"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={onClose}
              className="card-detail-action card-detail-action--secondary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CardDetailModal;
